/* Native Shopify tags determine membership. This file only composes filters and presents results. */
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const seen = new WeakSet();
const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)');

export function discoveryDestination(base, { audience = 'all', occasion = 'all', budget = 0 }, origin = location.origin) {
  const tags = [];
  if (['men', 'women', 'unisex'].includes(audience)) tags.push(`mag-shop-${audience}`);
  if (['date-night', 'office', 'everyday'].includes(occasion)) tags.push(`mag-occasion-${occasion}`);
  if (occasion === 'winter') tags.push('mag-season-winter');
  const url = new URL(base.replace(/\/$/, '') + (tags.length ? '/' + tags.join('+') : ''), origin);
  if (budget === 'above5000') url.searchParams.set('filter.v.price.gte', '5000.01');
  if (Number(budget) > 0) url.searchParams.set('filter.v.price.lte', String(Number(budget) / 100));
  return url;
}

export async function collectDiscoveryPages(destination, { limit = 6, budget = 0, signal, fetcher = fetch, parser = html => new DOMParser().parseFromString(html, 'text/html') } = {}) {
  let next = new URL(destination), cards = [], visited = new Set(), handles = new Set(), brands = new Set();
  next.searchParams.set('filter.v.availability', '1');
  while (next && cards.length < Math.max(limit * 16, 96) && brands.size < limit) {
    next.searchParams.set('section_id', 'mag-discovery-results');
    const key = next.href;
    if (visited.has(key)) break;
    visited.add(key);
    const response = await fetcher(key, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Selection could not be loaded.');
    const doc = parser(await response.text()), page = doc.querySelector('[data-r6-result-page]');
    if (!page) throw new Error('Selection could not be loaded.');
    for (const card of page.querySelectorAll('[data-mag-card]')) {
      const price = Number(card.dataset.price), handle = card.dataset.handle;
      if (!handle || handles.has(handle) || !Number.isFinite(price) || (Number(budget) > 0 && price > Number(budget)) || (budget === 'above5000' && price <= 500000)) continue;
      handles.add(handle); cards.push(card);
      if (card.querySelector('img')) brands.add((card.dataset.vendor || handle).trim().toLowerCase());
    }
    const candidate = page.dataset.next ? new URL(page.dataset.next, next) : null;
    if (candidate && candidate.origin !== destination.origin) throw new Error('Invalid selection destination.');
    if (candidate) {
      for (const [name, value] of destination.searchParams) if (name !== 'page' && name !== 'section_id') candidate.searchParams.set(name, value);
      candidate.searchParams.set('filter.v.availability', '1');
    }
    next = candidate;
  }
  cards.sort((a,b) => Number(!!b.querySelector('img')) - Number(!!a.querySelector('img')));
  const balanced = [], selected = new Set();
  for (const card of cards) {
    const brand = (card.dataset.vendor || card.dataset.handle).trim().toLowerCase();
    if (selected.has(brand)) continue;
    selected.add(brand); balanced.push(card);
    if (balanced.length === limit) return balanced;
  }
  for (const card of cards) { if (!balanced.includes(card)) balanced.push(card); if (balanced.length === limit) break; }
  return balanced;
}

function startDiscovery(root) {
  if (seen.has(root)) return; seen.add(root);
  const state = { audience: 'all', occasion: 'all', budget: 0 };
  const results = $('[data-r6-results]', root), status = $('[data-r6-status]', root), empty = $('[data-r6-empty]', root), browse = $('[data-r6-browse]', root);
  const occasionSelect = $('[data-r6-occasion]', root), occasionAny = $('[data-r6-occasion-any]', root), occasionOpen = $('[data-r6-occasion-open]', root), occasionChoices = $('[data-r6-occasion-choices]', root), occasionLabel = $('[data-r6-occasion-label]', root);
  const initialGrid = results.querySelector('.mag-product-grid')?.cloneNode(true);
  let controller, version = 0;
  const labels = { all: 'All fragrances', men: 'Men', women: 'Women', unisex: 'Unisex', 'date-night': 'Date night', office: 'Office', everyday: 'Everyday', winter: 'Winter edit' };
  function syncOccasion() {
    occasionSelect.value = state.occasion;
    occasionAny.setAttribute('aria-pressed', String(state.occasion === 'all'));
    occasionOpen.classList.toggle('is-selected', state.occasion !== 'all');
    occasionLabel.textContent = state.occasion === 'all' ? 'Select occasion' : labels[state.occasion];
    $$('[data-r6-occasion-choice]', occasionChoices).forEach(choice => choice.setAttribute('aria-pressed', String(choice.dataset.r6OccasionChoice === state.occasion)));
  }
  function animateSlider(field, next, selector) {
    const thumb = $(selector, field), prior = Number(field.dataset.active || 0); field.dataset.active = String(next);
    field.style.setProperty('--mag-active', String(next));
    if (!thumb) return;
    thumb.style.transform = `translateX(${next * 100}%)`;
    if (prior !== next && !reduced?.matches && thumb.animate) thumb.animate([
      { transform: `translateX(${prior * 100}%) scale(1)` },
      { transform: `translateX(${prior * 100 + Math.sign(next - prior) * 9}%) scale(1.15,.92)`, offset: .2 },
      { transform: `translateX(${next * 100 - Math.sign(next - prior) * 12}%) scale(1.18,.9)`, offset: .55 },
      { transform: `translateX(${next * 100 + Math.sign(next - prior) * 4}%) scale(.97,1.035)`, offset: .8 },
      { transform: `translateX(${next * 100}%) scale(1)` }
    ], { duration: 520, easing: 'cubic-bezier(.25,.65,.3,1)' });
  }
  async function update() {
    controller?.abort(); controller = new AbortController(); const run = ++version;
    const url = discoveryDestination(root.dataset.collectionUrl, state);
    browse.href = url.pathname + url.search;
    browse.textContent = 'Explore more · ' + (state.audience === 'all' ? 'All Fragrances' : labels[state.audience] + ' Fragrances') + ' ↗';
    const selection = [labels[state.audience], state.occasion === 'all' ? null : labels[state.occasion], state.budget === 'above5000' ? 'Above ₹5,000' : state.budget ? 'Up to ₹' + (state.budget / 100).toLocaleString('en-IN') : null].filter(Boolean).join(' · ');
    status.textContent = 'Finding available fragrances…'; results.setAttribute('aria-busy', 'true'); results.inert = true; empty.hidden = true;
    syncOccasion();
    if (state.audience === 'all' && state.occasion === 'all' && state.budget === 0 && initialGrid?.querySelector('[data-mag-card]')) {
      const count = initialGrid.querySelectorAll('[data-mag-card]').length;
      results.replaceChildren(initialGrid.cloneNode(true));
      results.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      status.textContent = `${count} available ${count === 1 ? 'pick' : 'picks'} · All fragrances`;
      results.setAttribute('aria-busy', 'false'); results.inert = false;
      return;
    }
    try {
      const cards = await collectDiscoveryPages(url, { budget: state.budget, signal: controller.signal });
      if (run !== version) return;
      const grid = document.createElement('div'); grid.className = 'mag-product-grid';
      grid.append(...cards.map(card => document.importNode(card, true))); results.replaceChildren(grid);
      results.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      status.textContent = (cards.length ? `${cards.length} available ${cards.length === 1 ? 'pick' : 'picks'} · ` : '') + selection;
      empty.hidden = cards.length > 0;
    } catch (error) {
      if (error.name === 'AbortError' || run !== version) return;
      results.replaceChildren(); status.textContent = 'We couldn’t refresh these picks. Open the full selection below.';
    } finally { if (run === version) { results.setAttribute('aria-busy', 'false'); results.inert = false; } }
  }
  $$('input[type="radio"]', root).forEach((input, index) => input.addEventListener('change', () => {
    if (!input.checked) return; state.audience = input.value;
    // The established audience animation remains in magnifiscent-v3.js.
    $('.mag-audience', root).style.setProperty('--mag-active', String(index)); update();
  }));
  function closeOccasions({ returnFocus = false } = {}) {
    if (returnFocus && !occasionChoices.hidden) occasionOpen.focus();
    occasionChoices.hidden = true;
    occasionOpen.setAttribute('aria-expanded', 'false');
  }
  function setOccasion(value) { state.occasion = value; syncOccasion(); update(); }
  occasionAny.addEventListener('click', () => { closeOccasions(); setOccasion('all'); });
  occasionOpen.addEventListener('click', () => {
    const opening = occasionChoices.hidden;
    occasionChoices.hidden = !opening;
    occasionOpen.setAttribute('aria-expanded', String(opening));
  });
  $$('[data-r6-occasion-choice]', occasionChoices).forEach(choice => choice.addEventListener('click', () => {
    const value = choice.dataset.r6OccasionChoice;
    closeOccasions({ returnFocus: true });
    setOccasion(value);
  }));
  root.addEventListener('keydown', event => { if (event.key === 'Escape' && !occasionChoices.hidden) { event.preventDefault(); closeOccasions({ returnFocus: true }); } });
  root.addEventListener('click', event => { if (!occasionChoices.hidden && !event.target.closest('.mag-occasion-filter,.mag-occasion-tabs')) closeOccasions(); });
  document.addEventListener('click', event => { if (!occasionChoices.hidden && !root.contains(event.target)) closeOccasions(); });
  $$('[data-r6-budget]', root).forEach((button, index) => button.addEventListener('click', () => {
    state.budget = button.dataset.r6Budget === 'above5000' ? 'above5000' : Number(button.dataset.r6Budget) || 0;
    $$('[data-r6-budget]', root).forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    $('[data-r6-budget-value]', root).value = String(state.budget);
    animateSlider(button.closest('.mag-budget-slider'), index, '.mag-budget-slider__thumb'); update();
  }));
  $('[data-r6-reset]', root)?.addEventListener('click', () => {
    state.audience = state.occasion = 'all'; state.budget = 0;
    closeOccasions(); syncOccasion();
    $$('input[type="radio"]', root).forEach(input => { input.checked = input.value === 'all'; });
    $('.mag-audience', root).style.setProperty('--mag-active', '0');
    $$('[data-r6-budget]', root).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.r6Budget === '0')));
    $('[data-r6-budget-value]', root).value = '0'; animateSlider($('.mag-budget-slider', root), 0, '.mag-budget-slider__thumb'); $('input[type="radio"][value="all"]', root).dispatchEvent(new Event('change', { bubbles: true }));
  });
  // INR budget brackets are deliberately hidden when a customer selects another currency.
  if (root.dataset.currency !== 'INR') $('.mag-budget-v3', root).hidden = true;
  update();
}

function startNavigation(head) {
  if (seen.has(head)) return; seen.add(head);
  const panel = $('.mag-navigation-r6', head), tabs = $('.mag-menu-tabs-r6', head), gallery = $('.mag-menu-gallery-r6', head), brand = $('.mag-brand-wordmark', head);
  let data; try { data = JSON.parse($('[data-mag-navigation-r6]')?.textContent || '{}'); } catch { return; }
  let group = 'shop', opened = false, timer, intentTimer, controller, request = 0, trigger = null, choiceKey = '';
  const cache = new Map();
  function menuPicks(nodes, item) {
    const requested = /mag-shop-(men|women|unisex)/.exec(item.url)?.[1];
    const scored = nodes.map((node, index) => {
      const handle = node.dataset.handle || new URL(node.href, location.origin).pathname.split('/').pop();
      const profile = window.magResearchedCatalog?.[handle] || window.magCatalogue?.[handle] || {};
      const audiences = Array.isArray(profile.audience) ? profile.audience : Array.isArray(profile.gender) ? profile.gender : [];
      const exact = requested && audiences.includes(requested);
      const only = exact && audiences.length === 1;
      const score = (requested ? only ? 100 : exact ? 70 : 0 : 0) + (node.querySelector('img') ? 20 : 0);
      return { node, index, score, vendor: (node.dataset.vendor || handle).toLowerCase() };
    }).sort((a, b) => b.score - a.score || a.index - b.index);
    const result = [], used = new Set();
    for (const candidate of scored) {
      if (used.has(candidate.vendor)) continue;
      result.push(candidate.node); used.add(candidate.vendor);
      if (result.length === 3) return result;
    }
    for (const candidate of scored) { if (!result.includes(candidate.node)) result.push(candidate.node); if (result.length === 3) break; }
    return result;
  }
  function close(restore = false) { opened = false; choiceKey = ''; clearTimeout(timer); clearTimeout(intentTimer); controller?.abort(); head.classList.remove('is-menu-open','is-brand-hover'); panel.inert = true; panel.hidden = true; $$('[data-r6-menu-open]', head).forEach(b => b.setAttribute('aria-expanded', 'false')); if (restore) (trigger || brand)?.focus(); }
  async function choose(item) {
    const key = group + ':' + item.url; if (choiceKey === key) return; choiceKey = key;
    $$('a', tabs).forEach(link => { if(link.dataset.label === item.label) link.setAttribute('aria-current','true'); else link.removeAttribute('aria-current'); });
    $('[data-r6-menu-title]', head).textContent = item.label;
    $('[data-r6-menu-description]', head).textContent = item.description || (group === 'houses' ? `Explore ${item.label}, from its familiar favourites to your next discovery.` : 'Find the fragrance that feels like you.');
    const logo = $('[data-r6-menu-logo]', head); logo.hidden = !item.logo; if (item.logo) { logo.src = item.logo; logo.alt = item.label; }
    const link = $('.mag-menu-link', head); link.href = item.url; const arrow = document.createElement('span'); arrow.className = 'mag-thin-arrow'; arrow.setAttribute('aria-hidden','true'); arrow.textContent = '↗'; link.replaceChildren(document.createTextNode(item.label === 'Your scent reading' ? 'Find my scent ' : 'Explore ' + item.label.toLowerCase() + ' '), arrow);
    controller?.abort(); controller = new AbortController(); const current = ++request; gallery.replaceChildren();
    if (group === 'discover') {
      if (item.image) { const a = document.createElement('a'), img = document.createElement('img'); a.href = item.url; img.src = item.image; img.alt = ''; img.width = 600; img.height = 300; a.append(img); gallery.append(a); }
      return;
    }
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin || (!url.pathname.includes('/collections/') && url.pathname !== '/search')) return;
    const searchPicks = url.pathname === '/search';
    if (!searchPicks) url.searchParams.set('section_id', 'mag-menu-picks-r6');
    gallery.setAttribute('aria-busy', 'true');
    try {
      let next = url; const items = [], visited = new Set();
      while (next && items.length < 100) {
        if (!searchPicks) next.searchParams.set('section_id', 'mag-menu-picks-r6');
        if (visited.has(next.href)) break;
        visited.add(next.href);
        let html = cache.get(next.href);
        if (!html) { const response = await fetch(next, { signal: controller.signal, headers: { Accept: 'text/html' } }); if (!response.ok) throw Error('Unavailable'); html = await response.text(); cache.set(next.href, html); }
        if (current !== request || !opened) return;
        const doc = new DOMParser().parseFromString(html, 'text/html'), picks = $('[data-r6-menu-picks]', doc);
        if (!picks && !searchPicks) break;
        if (picks) items.push(...Array.from(picks.children));
        else for (const card of $$('[data-mag-card]', doc).slice(0, 3)) {
          const imageLink = $('.mag-card__image a', card) || $('a', card);
          if (!imageLink) continue;
          const pick = doc.createElement('a'); pick.className = 'mag-menu-pick-r6'; pick.href = imageLink.href; pick.dataset.handle = card.dataset.handle; pick.dataset.vendor = card.dataset.vendor;
          const image = $('img', imageLink)?.cloneNode(true); if (image) { image.className = 'mag-bottle'; pick.append(image); }
          const title = doc.createElement('strong'); title.textContent = $('h3', card)?.textContent?.trim() || ''; pick.append(title);
          const price = doc.createElement('span'); price.textContent = $('.mag-card__price', card)?.textContent?.trim() || ''; pick.append(price); items.push(pick);
        }
        const candidate = picks?.dataset.next ? new URL(picks.dataset.next, next) : null;
        next = !searchPicks && candidate?.origin === location.origin ? candidate : null;
      }
      if (current === request && opened) gallery.replaceChildren(...menuPicks(items, item).map(node => document.importNode(node, true)));
    } catch (error) { if (error.name !== 'AbortError' && current === request) gallery.replaceChildren(); }
    finally { if (current === request) gallery.setAttribute('aria-busy', 'false'); }
  }
  function show(key, opener) {
    if (document.querySelector('theme-drawer[open],dialog[open]')) return;
    clearTimeout(timer); if (opened && group === key) return;
    const items = data[key] || []; if (!items.length) return;
    group = key; opened = true; trigger = opener || trigger; panel.hidden = false; panel.inert = false; head.classList.add('is-menu-open'); panel.dataset.group = key;
    $$('[data-r6-menu-open]', head).forEach(b => b.setAttribute('aria-expanded', String(b.dataset.r6MenuOpen === key)));
    tabs.replaceChildren();
    for (const item of items) {
      const link = document.createElement('a'); link.href = item.url; link.textContent = item.label; link.dataset.label = item.label;
      link.addEventListener('pointerenter', event => { clearTimeout(intentTimer); if (event.pointerType === 'mouse') intentTimer = setTimeout(() => choose(item), 320); });
      link.addEventListener('pointerleave', () => clearTimeout(intentTimer));
      link.addEventListener('focus', () => { clearTimeout(intentTimer); choose(item); });
      tabs.append(link);
    }
    choose(items[0]);
  }
  $$('[data-r6-menu-open]', head).forEach(button => {
    button.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && innerWidth >= 750) show(button.dataset.r6MenuOpen, button); });
    button.addEventListener('click', () => { if (opened && button.classList.contains('mag-hamburger')) close(); else show(button.dataset.r6MenuOpen, button); });
    button.addEventListener('keydown', event => { if (event.key === 'ArrowDown') { event.preventDefault(); show(button.dataset.r6MenuOpen, button); $('a', tabs)?.focus(); } });
  });
  brand.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && innerWidth >= 750) head.classList.add('is-brand-hover'); });
  $('.mag-menu-detail-r6', head).addEventListener('pointerenter', () => clearTimeout(intentTimer));
  head.addEventListener('pointerenter', () => clearTimeout(timer));
  head.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') timer = setTimeout(close, 300); });
  $('[data-r6-menu-close]', head).addEventListener('click', () => close(true));
  head.addEventListener('keydown', event => { if (event.key === 'Escape') close(true); });
  head.addEventListener('focusout', () => setTimeout(() => { if (!head.contains(document.activeElement)) close(); }, 0));
  document.addEventListener('click', event => { if (!head.contains(event.target)) close(); });
  document.addEventListener('theme-drawer:open', () => close());
  if (window.Shopify?.designMode) document.addEventListener('shopify:section:unload', event => { if (event.target.contains(head)) close(); });
}

function startMarquee(root) {
  if (seen.has(root)) return; seen.add(root);
  const button = $('[data-mag-marquee-pause]', root); if (!button) return;
  button.addEventListener('click', () => { const paused = root.classList.toggle('is-paused'); button.textContent = paused ? 'Play' : 'Pause'; button.setAttribute('aria-label', paused ? 'Play moving message' : 'Pause moving message'); button.setAttribute('aria-pressed', String(paused)); });
}
function boot() { $$('[data-r6-house-edit]').forEach(section => { section.hidden = !$('[data-mag-card]', section); }); $$('[data-mag-discovery-r6]').forEach(startDiscovery); $$('[data-mag-header-r6]').forEach(startNavigation); $$('[data-mag-marquee]').forEach(startMarquee); }
if (typeof document !== 'undefined') { if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot(); document.addEventListener('shopify:section:load', boot); }
